<script lang="ts">
  import Button from "../shared/Button.svelte";
  import type { OpportunityDetailed } from "./types";
  import { formatTime } from "./utils";

  export let opportunity: OpportunityDetailed | null = null;
  export let retrying: boolean = false;
  export let onClose: () => void = () => {};
  export let onRetrySync: (opportunity: any) => void = () => {};
  export let title: string = "Details";
  export let labels: any = {};
  
  // New props for varieties
  export let type: "opportunity" | "lead" | "generic" | "activity" = "opportunity";
  export let data: any = null;
  export let fields: Array<{ label: string; key: string; format?: "datetime" | "badge" | "status" | "text" }> = [];
  export let config: { 
    showRetry?: boolean; 
    showBadge?: boolean;
    headerColor?: string;
  } = {};

  const defaultLabels = {
    awsId: "AWS ID",
    acelinkId: "Acelink ID",
    lastUpdate: "Last Update",
    awsSync: "AWS Sync",
    acelinkSync: "Acelink Sync",
    syncErrorTitle: "Sync Error",
    syncErrorMessage: "Sync validation failed. Please review the details and try again.",
    retryBtnText: "Retry Sync",
    retryingBtnText: "Retrying..."
  };

  $: activeLabels = { ...defaultLabels, ...labels };
  $: displayData = data || opportunity;
  $: headerText = displayData?.name || displayData?.title || displayData?.label || title;
  $: currentStatus = displayData?.status || displayData?.state;
  $: displayFields = fields.length > 0 ? fields : (displayData ? Object.keys(displayData).map(k => ({ 
    label: k.charAt(0).toUpperCase() + k.slice(1).replace(/([A-Z])/g, ' $1'), 
    key: k, 
    format: "text" as const 
  })) : []);

  const handleClose = () => onClose();
  const handleRetrySync = () => onRetrySync(displayData);

</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
<div
  class="drawer-overlay fixed inset-0 z-[1000] flex justify-end animate-[fadeIn_0.2s]"
  on:click={handleClose}
  on:keydown={(e) => e.key === 'Escape' && handleClose()}
  role="button"
  tabindex="0"
>
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="drawer-panel w-[500px] max-w-[90vw] flex flex-col animate-[slideIn_0.3s]"
    on:click|stopPropagation
    role="dialog"
  >
    <div
      class="flex justify-between items-center p-6 border-b border-white/10 bg-[#1b1e2e] gap-4"
      style={config.headerColor ? `background-color: ${config.headerColor}` : ''}
    >
      <div class="flex flex-row gap-4 flex-1 min-w-0">
        <h3
          class="text-[1.125rem] font-bold text-white m-0 overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {headerText}
        </h3>
        {#if type === "opportunity" || config.showBadge}
          <span 
            class="inline-flex items-center px-3 py-1.5 rounded-2xl text-[0.813rem] font-semibold w-fit"
            class:badge-synced={currentStatus === "synced"}
            class:badge-pending={currentStatus === "not_synced" || currentStatus === "pending"}
            class:badge-error={currentStatus === "error" || currentStatus === "failed"}
          >
            {#if currentStatus === "synced"}
              🟢 Synced
            {:else if currentStatus === "not_synced" || currentStatus === "pending"}
              🟡 Pending
            {:else if currentStatus === "error" || currentStatus === "failed"}
              🔴 Error
            {:else}
              ⚪ {currentStatus || "Unknown"}
            {/if}
          </span>
        {/if}
      </div>
      <button
        class="bg-transparent border-none text-white/80 text-[1.5rem] cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded transition-all hover:bg-white/10 hover:text-white"
        on:click={handleClose}
        aria-label="Close drawer"
      >
        ✕
      </button>
    </div>
    <div class="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
      {#if displayData}
        <div
          class="bg-black/20 border border-white/10 rounded-lg overflow-hidden"
        >
          {#if type === "opportunity"}
            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-white/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.awsId}
              </span>
              <span class="text-white font-semibold text-[0.938rem] text-right">
                {displayData.awsOppId || "—"}
              </span>
            </div>
            
            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-white/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.acelinkId}
              </span>
              <span class="text-white font-semibold text-[0.938rem] text-right">
                {displayData.id || "—"}
              </span>
            </div>
            
            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-white/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.lastUpdate}
              </span>
              <span class="text-white font-semibold text-[0.938rem] text-right">
                {formatTime(displayData.lastUpdate)}
              </span>
            </div>

            <div class="h-px bg-white/10 my-2" />

            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-white/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.awsSync}
              </span>
              <div
                class={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold ${
                  displayData.awsSynced
                    ? 'bg-green-500/10 border border-green-500/30'
                    : 'bg-red-500/10 border border-red-500/30'
                }`}
              >
                {#if displayData.awsSynced}
                  <span class="text-green-500 text-base font-bold">✓</span>
                  <span class="text-green-500">Synced</span>
                {:else}
                  <span class="text-red-500 text-base font-bold">✕</span>
                  <span class="text-red-500">Not Synced</span>
                {/if}
              </div>
            </div>
            
            <div
              class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-white/5"
            >
              <span class="text-white/60 text-sm font-semibold">
                {activeLabels.acelinkSync}
              </span>
              <div
                class={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold ${
                  displayData.acelinkSynced
                    ? 'bg-green-500/10 border border-green-500/30'
                    : 'bg-red-500/10 border border-red-500/30'
                }`}
              >
                {#if displayData.acelinkSynced}
                  <span class="text-green-500 text-base font-bold">✓</span>
                  <span class="text-green-500">Synced</span>
                {:else}
                  <span class="text-red-500 text-base font-bold">✕</span>
                  <span class="text-red-500">Not Synced</span>
                {/if}
              </div>
            </div>

            {#if displayData.recommendation}
              <div
                class="px-5 py-4 border-b border-white/5 transition-colors hover:bg-white/5"
              >
                <span class="block text-white/60 text-sm font-semibold mb-2">
                  Recommendation
                </span>
                <p class="text-white/90 text-sm leading-relaxed whitespace-pre-wrap m-0">
                  {displayData.recommendation}
                </p>
              </div>
            {/if}
          {:else if type === "generic" || type === "lead" || type === "activity"}
            {#each displayFields as field}
              {#if displayData[field.key] !== undefined && field.key !== 'name' && field.key !== 'id'}
                <div
                  class="flex justify-between items-center px-5 py-4 border-b border-white/5 transition-colors hover:bg-white/5"
                >
                  <span class="text-white/60 text-sm font-semibold">
                    {field.label}
                  </span>
                  <span class="text-white font-semibold text-[0.938rem] text-right">
                    {#if field.format === 'datetime'}
                      {formatTime(displayData[field.key])}
                    {:else if field.format === 'badge'}
                       <span class="px-2 py-1 rounded-md bg-white/10 text-xs">
                         {displayData[field.key]}
                       </span>
                    {:else}
                      {displayData[field.key] || "—"}
                    {/if}
                  </span>
                </div>
              {/if}
            {/each}
          {/if}
        </div>

        {#if displayData.status === "error" || displayData.syncError}
          <div
            class="bg-red-500/10 border border-red-500/30 rounded-lg p-5"
          >
            <div class="flex items-start gap-4">
              <span class="text-[1.5rem] flex-shrink-0">⚠️</span>
              <div>
                <h4 class="text-[0.938rem] font-bold text-white mb-2">
                  {activeLabels.syncErrorTitle || "Sync Error"}
                </h4>
                <p class="text-white/85 m-0 text-sm leading-relaxed">
                  {displayData.syncErrorMessage || activeLabels.syncErrorMessage || "Sync validation failed. Please review the details and try again."}
                </p>
              </div>
            </div>
          </div>
        {/if}

        {#if type === "opportunity" && displayData.status !== "synced" || config.showRetry}
          <div class="flex gap-4">
            <Button size="lg" block on:click={handleRetrySync} disabled={retrying}>
              {#if retrying}
                <span
                  class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
                />
                {activeLabels.retryingBtnText || "Retrying..."}
              {:else}
                {activeLabels.retryBtnText || "Retry Sync"}
              {/if}
            </Button>
          </div>
        {/if}
      {:else}
        <div class="text-center py-12 text-white/50">
          <p>Loading details...</p>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .drawer-overlay {
    background: rgba(5, 8, 16, 0.6);
    z-index: 1000;
  }

  .drawer-panel {
    background: #24283a;
    box-shadow: -8px 0 28px rgba(0, 0, 0, 0.55);
  }
</style>
